import React from 'react';

/**
 * The console's primary surface: a panel sitting proud of the page on an offset
 * shadow, with a hairline edge and a low corner radius.
 *
 * Every card, table wrapper and dashboard tile rests on this. The shadow is not
 * a hover affordance, it is structural: it separates the objects on the desk
 * from the desk itself, and it is the one place the system allows itself
 * personality. Do not flatten it.
 */
export interface PanelProps {
  className?: string;
  /** Small uppercase kicker above the title. */
  eyebrow?: string;
  title?: React.ReactNode;
  /** Rendered at the right of the header band. */
  actions?: React.ReactNode;
  /** Drop the inner padding when the child manages its own (a table, a chart). */
  flush?: boolean;
  children?: React.ReactNode;
}

/**
 * The surface itself, without the header/body structure.
 *
 * Some surfaces cannot use <Panel> even though they must look identical to it:
 * DataCatalog's table is a `flex-1 min-h-0` scroll child, so the extra
 * `panel-body` wrapper Panel renders would break the chain that keeps the page
 * from scrolling. They compose this string instead, so the look still has
 * exactly one definition.
 */
export const panelSurface = 'bg-panel border border-soft rounded-card shadow-chunky';

export const Panel = ({ className, eyebrow, title, actions, flush, children }: PanelProps) => (
  <div className={`panel ${panelSurface}${className ? ` ${className}` : ''}`}>
    {(eyebrow || title || actions) && (
      <div className="panel-header flex items-center justify-between gap-3 border-b border-soft px-4 py-3">
        <div className="min-w-0">
          {eyebrow && <div className="eyebrow text-fg-3">{eyebrow}</div>}
          {title && <div className="truncate text-[15px] font-bold text-fg-1">{title}</div>}
        </div>
        {actions && <div className="flex shrink-0 items-center gap-2">{actions}</div>}
      </div>
    )}
    <div className={flush ? 'panel-body' : 'panel-body p-4'}>{children}</div>
  </div>
);
